import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Pencil, Copy, Upload } from "lucide-react";
import { toast } from "sonner";
import { settingsRequest } from "../../lib/adminSettings";
import { ISSUE_LABEL } from "../../lib/api";
import { devLog } from "../../lib/log";
import VehiclePriceEditor, { validateVehiclePrice } from "./VehiclePriceEditor";
import VehiclePriceImport, { parseCSV } from "./VehiclePriceImport";

const CURRENT_YEAR = new Date().getFullYear();

const ISSUE_TYPES = [
  { key: "lost_key", label: "Pérdida total de llave" },
  { key: "not_working", label: "Llave no funciona" },
  { key: "locked_inside", label: "Apertura (llaves dentro)" },
  { key: "key_copy", label: "Copia de llave" },
];

const emptyForm = { id: "", brand: "", model: "", year_from: 2000, year_to: CURRENT_YEAR, issue_type: "lost_key", price: 0 };

export default function VehiclePricesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [issueFilter, setIssueFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await settingsRequest("get", "/admin/vehicle-prices");
      setItems(res.data || []);
    } catch (e) {
      devLog.error("Error loading vehicle prices:", e);
      toast.error("Error al cargar precios de vehículos");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const startNew = () => setEditing({ ...emptyForm });
  const startEdit = (item) => setEditing({ ...item });
  const duplicate = (item) => setEditing({ ...item, id: "" });

  const save = async () => {
    const err = validateVehiclePrice(editing);
    if (err) return toast.error(err);
    setSaving(true);
    const payload = {
      brand: editing.brand.trim(),
      model: editing.model.trim(),
      year_from: Number(editing.year_from),
      year_to: Number(editing.year_to),
      issue_type: editing.issue_type,
      price: Number(editing.price),
    };
    try {
      if (editing.id) await settingsRequest("put", `/admin/vehicle-prices/${editing.id}`, payload);
      else await settingsRequest("post", "/admin/vehicle-prices", payload);
      toast.success(editing.id ? "Precio actualizado" : "Precio añadido");
      setEditing(null);
      load();
    } catch (e) {
      devLog.error("Error saving vehicle price:", e);
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar el precio para ${item.brand} ${item.model} (${item.year_from}-${item.year_to})?`)) return;
    try {
      await settingsRequest("delete", `/admin/vehicle-prices/${item.id}`);
      toast.success("Precio eliminado");
      load();
    } catch (e) {
      devLog.error("Error deleting vehicle price:", e);
      toast.error("Error al eliminar");
    }
  };

  const doImport = async () => {
    const { items: parsed, errors: parseErrors } = parseCSV(importText);
    if (parseErrors.length && parsed.length === 0) {
      setImportResult({ created: 0, total: 0, errors: parseErrors.map((e, i) => ({ row: i + 1, error: e })) });
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const res = await settingsRequest("post", "/admin/vehicle-prices/bulk", { items: parsed });
      const combined = {
        created: res.data.created,
        total: res.data.total,
        errors: [...parseErrors.map((e, i) => ({ row: `parseo #${i + 1}`, error: e })), ...(res.data.errors || [])],
      };
      setImportResult(combined);
      if (combined.created > 0) toast.success(`${combined.created} precios importados`);
      load();
    } catch (e) {
      devLog.error("Error importing CSV:", e);
      toast.error(e.response?.data?.detail || "Error al importar");
    } finally { setImporting(false); }
  };

  const filtered = items.filter((it) => {
    if (issueFilter !== "all" && it.issue_type !== issueFilter) return false;
    if (!filter.trim()) return true;
    const q = filter.trim().toLowerCase();
    return it.brand.toLowerCase().includes(q) || it.model.toLowerCase().includes(q);
  });

  return (
    <div className="sc-card rounded-md p-6" data-testid="vehicle-prices-section">
      <div className="flex items-start justify-between mb-1">
        <div>
          <h3 className="font-display font-semibold text-lg">Precios por vehículo</h3>
          <p className="text-xs text-[#71717A] mt-1">
            Precios sin IVA por marca + modelo + rango de años. Al presupuestar se le añade IVA 21% + desplazamiento.
            Si no hay coincidencia, el cliente ve un botón de WhatsApp para presupuesto personalizado.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setImportOpen(true)} className="btn-outline px-4 py-2 rounded-md font-semibold text-sm flex items-center gap-1" data-testid="import-csv-btn">
            <Upload className="w-3.5 h-3.5" /> Importar CSV
          </button>
          <button onClick={startNew} className="btn-amber px-4 py-2 rounded-md font-semibold text-sm flex items-center gap-1" data-testid="add-vehicle-price-btn">
            <Plus className="w-3.5 h-3.5" /> Añadir precio
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mt-5 mb-4">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Buscar por marca o modelo…"
          className="flex-1 min-w-[200px] bg-[#141414] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#FFC107] outline-none"
          data-testid="vp-filter-input"
        />
        <select
          value={issueFilter}
          onChange={(e) => setIssueFilter(e.target.value)}
          className="bg-[#141414] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#FFC107] outline-none"
          data-testid="vp-filter-issue"
        >
          <option value="all">Todos los servicios</option>
          {ISSUE_TYPES.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
        </select>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 text-[#FFC107] animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-8 text-center text-sm text-[#71717A]">
          {items.length === 0 ? "No hay precios configurados aún. Añade el primero →" : "Sin resultados para este filtro."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vp-table">
            <thead>
              <tr className="text-left text-xs font-mono uppercase tracking-wider text-[#71717A] border-b border-white/10">
                <th className="py-2 pr-2">Marca</th>
                <th className="py-2 pr-2">Modelo</th>
                <th className="py-2 pr-2 text-center">Años</th>
                <th className="py-2 pr-2">Servicio</th>
                <th className="py-2 pr-2 text-right">Precio (sin IVA)</th>
                <th className="py-2 pr-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`vp-row-${it.id}`}>
                  <td className="py-2.5 pr-2 font-medium">{it.brand}</td>
                  <td className="py-2.5 pr-2">{it.model}</td>
                  <td className="py-2.5 pr-2 text-center text-[#A1A1AA] font-mono">{it.year_from} – {it.year_to}</td>
                  <td className="py-2.5 pr-2 text-[#A1A1AA]">{ISSUE_LABEL[it.issue_type] || it.issue_type}</td>
                  <td className="py-2.5 pr-2 text-right font-mono text-[#FFC107]">{Number(it.price).toFixed(2)} €</td>
                  <td className="py-2.5 pr-2 text-right">
                    <button onClick={() => duplicate(it)} className="text-[#A1A1AA] hover:text-white p-1.5 mr-1" data-testid={`vp-duplicate-${it.id}`} title="Duplicar">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => startEdit(it)} className="text-[#A1A1AA] hover:text-white p-1.5 mr-1" data-testid={`vp-edit-${it.id}`} title="Editar">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(it)} className="text-[#EF4444] hover:bg-[#EF4444]/10 p-1.5 rounded-md" data-testid={`vp-delete-${it.id}`} title="Eliminar">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <VehiclePriceEditor editing={editing} setEditing={setEditing} onSave={save} saving={saving} />
      <VehiclePriceImport
        open={importOpen}
        onClose={() => setImportOpen(false)}
        importText={importText}
        setImportText={setImportText}
        importing={importing}
        importResult={importResult}
        setImportResult={setImportResult}
        onImport={doImport}
      />

      <style>{`
        .vp-input { width: 100%; background: #141414; border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; padding: 10px 12px; color: #fff; font-size: 14px; outline: none; }
        .vp-input:focus { border-color: #FFC107; }
      `}</style>
    </div>
  );
}
